import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { http } from "@/lib/api";
import { Barbell, Gift } from "@phosphor-icons/react";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [refCode, setRefCode] = useState("");
  const [refDisplay, setRefDisplay] = useState("");

  useEffect(() => {
    let code = "";
    try {
      code = (localStorage.getItem("lazylean_ref_code") || "").trim().toUpperCase();
    } catch {
      code = "";
    }
    if (!code) return;
    setRefCode(code);
    (async () => {
      try {
        const { data } = await http.get(`/referrals/resolve/${code}`);
        if (data?.valid) setRefDisplay(data.display_name);
      } catch {
        /* invalid code → silently drop */
      }
    })();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await register(email, password, name, refCode || undefined);
      // clear captured code after successful signup
      try { localStorage.removeItem("lazylean_ref_code"); } catch { /* ignore */ }
      navigate("/app", { replace: true });
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-[#0A0A0A]">
      <div className="flex items-center justify-center p-6 md:p-12 order-2 md:order-1">
        <div className="w-full max-w-md">
          <div className="md:hidden flex items-center gap-2 mb-8">
            <div className="w-8 h-8 bg-[#FF3333] flex items-center justify-center">
              <Barbell weight="bold" size={18} color="#fff" />
            </div>
            <span className="font-oswald text-xl font-bold uppercase tracking-widest">Lazy Lean Method<sup className="font-manrope text-[11px] font-semibold tracking-normal ml-0.5 -top-1.5 relative">™</sup></span>
          </div>

          <div className="id-label mb-3">— Start your method —</div>
          <h2 className="id-h2 mb-2">Create account</h2>
          <p className="text-zinc-400 mb-6 font-manrope text-sm">Sign up to start the 7 Lazy Lean Habits™ and get your coach assignment.</p>

          {refDisplay && (
            <div className="border border-[#FF3333] bg-[#FF3333]/10 px-4 py-3 mb-6 flex items-center gap-3" data-testid="referral-banner">
              <Gift weight="fill" size={20} className="text-[#FF3333] shrink-0" />
              <div className="text-sm font-manrope">
                <div className="text-white font-semibold">Referred by {refDisplay}.</div>
                <div className="text-zinc-400 text-xs mt-0.5">They&apos;ll get a free week when you complete your first payment.</div>
              </div>
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-5" data-testid="register-form">
            <div>
              <label className="id-label block mb-2">Full name</label>
              <input type="text" required value={name} onChange={(e) => setName(e.target.value)}
                className="id-input" placeholder="Your name" data-testid="register-name-input" />
            </div>
            <div>
              <label className="id-label block mb-2">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="id-input" placeholder="you@example.com" data-testid="register-email-input" />
            </div>
            <div>
              <label className="id-label block mb-2">Password</label>
              <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                className="id-input" placeholder="At least 6 characters" data-testid="register-password-input" />
            </div>

            {err && (
              <div className="border border-[#FF3333] bg-[#FF3333]/10 px-4 py-3 text-sm text-[#FF3333] font-manrope" data-testid="register-error">
                {err}
              </div>
            )}

            <button type="submit" disabled={loading} className="id-btn-primary w-full" data-testid="register-submit-button">
              {loading ? "Creating..." : "Create Account"}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-[#262626]">
            <p className="text-sm text-zinc-400 font-manrope">
              Already a member?{" "}
              <Link to="/login" data-testid="link-to-login" className="text-[#FF3333] hover:text-[#CC0000] font-semibold uppercase tracking-wider text-xs">
                Sign in →
              </Link>
            </p>
          </div>
        </div>
      </div>

      <div className="hidden md:block relative overflow-hidden border-l border-[#262626] order-1 md:order-2">
        <div className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1672344048213-76b6e77304bd)' }} />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative h-full flex flex-col justify-end p-12">
          <div className="id-label text-zinc-400 mb-3">— The Promise —</div>
          <h1 className="id-h1 max-w-md">
            7 Habits.<br/>
            <span className="text-[#FF3333]">First 10 kg gone.</span><br/>
            No diet.
          </h1>
          <p className="text-zinc-400 max-w-md mt-6 font-manrope">
            For men carrying some weight, juggling busy lives, raising kids — or anyone wanting a brotherhood that actually finishes what it starts. Build habits that stick, and finally see the scale move.
          </p>
        </div>
      </div>
    </div>
  );
}
